import React, { useEffect, useState } from "react";
import {
  TextInput,
  SelectInput,
  useForm,
} from "../../Components/Inputs";
import Btn from "../../../../Components/Buttons/Btn";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../Hooks/useAxios";
import init_shipment from "../init_shipment";

const EditShipment = () => {
  const { id } = useParams();
  const [input, setInput] = useState(init_shipment);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  const navigate = useNavigate();
  const request = useAxios();

  // Fetch options for dropdowns
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch customers
        const customerRes = await request({ 
          method: "GET", 
          url: "customers/all" 
        });
        
        // Fetch vehicles
        const vehicleRes = await request({ 
          method: "GET", 
          url: "vehicles/all-vehicles" 
        });
        
        // Fetch all members and filter
        const membersRes = await request({ 
          method: "GET", 
          url: "members/all" 
        });

        if (customerRes !== "error" && customerRes.docs) {
          setCustomers(customerRes.docs);
        }
        
        if (vehicleRes !== "error" && vehicleRes.docs) {
          setVehicles(vehicleRes.docs);
        }
        
        if (membersRes !== "error" && membersRes.docs) {
          const driversList = membersRes.docs.filter(member => 
            member.role_id === 5 || member.role === "driver" || member.role === "Driver"
          );
          setDrivers(driversList);
          
          const dispatchersList = membersRes.docs.filter(member => 
            member.role_id === 2 || member.role === "dispatcher" || member.role === "Dispatcher"
          );
          setDispatchers(dispatchersList);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, [request]); // Added request to dependencies

  // Fetch shipment details
  useEffect(() => {
    const getShipmentDetails = async () => {
      try {
        const res = await request({
          method: "GET",
          url: `shipments/single-shipment/${id}`,
        });

        if (res === "error") {
          const res2 = await request({
            method: "GET",
            url: `shipments/${id}`,
          });
          
          if (res2 === "error") {
            console.error("Shipment not found");
            return;
          }
          
          populateForm(res2);
        } else {
          populateForm(res);
        }
      } catch (error) {
        console.error("Error fetching shipment:", error);
      } finally {
        setLoadingData(false);
      }
    };

    const populateForm = (shipmentData) => {
      const inputCpy = { ...init_shipment };
      
      const shipment = shipmentData.shipment || shipmentData;
      
      inputCpy.customer_id.value = shipment?.customer_id?.toString() || "";
      inputCpy.vehicle_id.value = shipment?.vehicle_id?.toString() || "";
      inputCpy.driver_id.value = shipment?.driver_id?.toString() || "";
      inputCpy.dispatcher_id.value = shipment?.dispatcher_id?.toString() || "";
      inputCpy.origin.value = shipment?.origin || "";
      inputCpy.destination.value = shipment?.destination || "";
      inputCpy.status.value = shipment?.status || "pending";
      inputCpy.total_price.value = shipment?.total_price?.toString() || "";

      setInput(inputCpy);
    };

    if (id) {
      getShipmentDetails();
    } else {
      setLoadingData(false);
    }
  }, [id, request]); // Added request to dependencies

  const handleSubmit = async (data) => {
    setIsLoading(true);
    
    try {
      const payload = {
        customer_id: data.customer_id.value,
        vehicle_id: data.vehicle_id.value || null,
        driver_id: data.driver_id.value || null,
        dispatcher_id: data.dispatcher_id.value || null,
        origin: data.origin.value,
        destination: data.destination.value,
        status: data.status.value,
        total_price: parseFloat(data.total_price.value) || 0,
      };

      console.log("Updating shipment with:", payload);

      const res = await request({
        method: "PUT",
        url: `shipments/update/${id}`,
        body: payload,
        show_loading: false,
      });

      if (res === "error") {
        alert("Failed to update shipment. Check console for details.");
        return;
      }

      alert("Shipment updated successfully!");
      navigate("/dashboard/shipments");
    } catch (error) {
      console.error("Error updating shipment:", error);
      alert("Error updating shipment: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const { handleValidation, validate } = useForm({
    input,
    submit: handleSubmit,
  });

  if (loadingData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading shipment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shadow-2xl w-[85%] m-auto border-t p-4 mt-12 rounded-md bg-white">
      <h3 className="text-center text-xl font-semibold text-primary">
        Edit Shipment #{id}
      </h3>
      <div className="mt-4 flex flex-col gap-5">
        <TextInput
          input={input}
          setInput={setInput}
          field="origin"
          validate={validate}
          placeholder="Origin"
          required={true}
        />
        <TextInput
          input={input}
          setInput={setInput}
          field="destination"
          validate={validate}
          placeholder="Destination"
          required={true}
        />
        <TextInput
          input={input}
          setInput={setInput}
          field="total_price"
          validate={validate}
          placeholder="Total Price"
          type="number"
          min="0"
          step="0.01"
        />

        <SelectInput
          input={input}
          setInput={setInput}
          field="customer_id"
          options={[
            { value: "", label: "Select Customer" },
            ...customers.map((c) => ({ 
              value: c.id.toString(), 
              label: `${c.name}${c.company_name ? ` — ${c.company_name}` : ''}` 
            }))
          ]}
          placeholder="Select Customer"
          required={true}
        />
        <SelectInput
          input={input}
          setInput={setInput}
          field="vehicle_id"
          options={[
            { value: "", label: "Select Vehicle (optional)" },
            ...vehicles.map((v) => ({ 
              value: v.id.toString(), 
              label: `${v.registration_no || v.plate_no || 'Vehicle'}${v.make ? ` - ${v.make}` : ''}` 
            }))
          ]}
          placeholder="Select Vehicle"
        />
        <SelectInput
          input={input}
          setInput={setInput}
          field="driver_id"
          options={[
            { value: "", label: "Select Driver (optional)" },
            ...drivers.map((d) => ({ 
              value: d.id.toString(), 
              label: `${d.name || d.full_name}${d.email ? ` (${d.email})` : ''}` 
            }))
          ]}
          placeholder="Select Driver"
        />
        <SelectInput
          input={input}
          setInput={setInput}
          field="dispatcher_id"
          options={[
            { value: "", label: "Select Dispatcher (optional)" },
            ...dispatchers.map((d) => ({ 
              value: d.id.toString(), 
              label: `${d.name || d.full_name}${d.email ? ` (${d.email})` : ''}` 
            }))
          ]}
          placeholder="Select Dispatcher"
        />
        
        <SelectInput
          input={input}
          setInput={setInput}
          field="status"
          options={[
            { value: "PENDING", label: "Pending" },
            { value: "IN_TRANSIT", label: "In Transit" },
            { value: "DELIVERED", label: "Delivered" },
            { value: "CANCELLED", label: "Cancelled" }
          ]}
          placeholder="Select Status"
        />
      </div>
      <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
        <Btn 
          onClick={() => navigate("/dashboard/shipments")}
          variant="secondary"
          disabled={isLoading}
        >
          Cancel
        </Btn>
        <Btn 
          onClick={handleValidation} 
          disabled={isLoading || loadingData}
          isLoading={isLoading}
        >
          {isLoading ? "Updating..." : "Update Shipment"}
        </Btn>
      </div>
    </div>
  );
};

export default EditShipment;